import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import socket, { configureSocketAuth } from "../../socket";
import { getNotification } from "../../Redux/actions/commonActtion";
import { getProjectDetails } from "../../Redux/actions/projectActions";
import { receiveProjectStatusUpdate } from "../../Redux/actions/projectsActions";
import CustomToast from "../CustomToast/CustomToast";
import "../CustomToast/CustomToast.css";
import { getRenderHistoryPath } from "../../Pages/utils/renderHistoryNavigation";

// Allows for clock drift between the server's updatedAt and this browser.
const MISSED_RENDER_GRACE_MS = 30000;

const isTrue = (value) => value === true || value === "true" || value === 1;

const getStatusFromRenderUpdate = (data) => {
  if (isTrue(data?.isVideoGenerated)) {
    return { projectStatus: "Rendered", statusId: 2 };
  }
  if (!isTrue(data?.isRederFinished)) {
    return { projectStatus: "In Progress", statusId: 1 };
  }
  return { projectStatus: "Draft", statusId: 0 };
};

export default function RenderCompleteListener() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentProjectId = useSelector((state) => state?.project?._id);
  const isCard = useSelector((state) => state?.project?.isCard);
  // Changes on in-app login/logout, which reconnects the socket for that user.
  const signedInUserId = useSelector((state) =>
    state?.userInfo?.isAuthenticated ? state?.userInfo?.userInfo?._id : null,
  );
  const [notice, setNotice] = useState(null);
  const shownRenderIdsRef = useRef(new Set());
  const openProjectRef = useRef({ currentProjectId, isCard });
  openProjectRef.current = { currentProjectId, isCard };

  useEffect(() => {
    if (socket.connected) socket.disconnect();
    if (localStorage.getItem("user") && configureSocketAuth()) socket.connect();
  }, [signedInUserId]);

  useEffect(() => {
    const handleProjectStatusUpdate = (data) => {
      if (data?.projectId && data?.projectStatus) {
        dispatch(receiveProjectStatusUpdate(data));
      }
    };

    const handleRenderUpdate = (data) => {
      if (data?.projectId) {
        const fallback = getStatusFromRenderUpdate(data);
        handleProjectStatusUpdate({
          ...data,
          projectStatus: data.projectStatus || fallback.projectStatus,
          statusId: data.statusId ?? fallback.statusId,
        });
      }

      const videoGenerated = isTrue(data?.isVideoGenerated);
      if (!videoGenerated && !isTrue(data?.isRederFinished)) return;
      if (data?.renderId) {
        const renderId = String(data.renderId);
        if (shownRenderIdsRef.current.has(renderId)) return;
        shownRenderIdsRef.current.add(renderId);
      }

      const titleSuffix = data?.title ? ` for project ${data.title}` : "";
      const message =
        data?.notification?.message ||
        (videoGenerated
          ? `Your render is complete${titleSuffix}. Please check your Render History.`
          : `Your render could not be completed${titleSuffix}. Please check your Render History.`);

      setNotice({
        message,
        renderHistoryPath: getRenderHistoryPath({
          projectId: data?.projectId,
          renderId: data?.renderId,
          isCard: data?.isCard,
        }),
      });
      dispatch(getNotification({ page_no: 1, limit: 20, append: false }));

      const { currentProjectId: openId, isCard: openIsCard } =
        openProjectRef.current;
      if (openId && String(openId) === String(data?.projectId)) {
        dispatch(getProjectDetails(openId, openIsCard));
      }
    };

    // Completions that finished while the socket was down are only saved as
    // notifications, so check for them once when the socket comes back.
    let disconnectedAt = null;
    const handleDisconnect = () => {
      disconnectedAt = Date.now();
    };
    const handleConnect = async () => {
      if (!disconnectedAt) return;
      const since = disconnectedAt - MISSED_RENDER_GRACE_MS;
      disconnectedAt = null;

      const notifications = await dispatch(
        getNotification({ page_no: 1, limit: 20, append: false, silent: true }),
      );
      if (!Array.isArray(notifications)) return;
      notifications
        .filter(
          (n) =>
            n?.notification_type === "render-complete" &&
            new Date(n.updatedAt).getTime() >= since,
        )
        .reverse()
        .forEach((n) =>
          handleRenderUpdate({
            renderId: n.render_id,
            projectId: n.project_id,
            isCard: n.is_card,
            isVideoGenerated: n.is_video_generated,
            isRederFinished: true,
            notification: n,
          }),
        );
    };

    socket.on("render-update", handleRenderUpdate);
    socket.on("project-status-update", handleProjectStatusUpdate);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect", handleConnect);
    return () => {
      socket.off("render-update", handleRenderUpdate);
      socket.off("project-status-update", handleProjectStatusUpdate);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect", handleConnect);
    };
  }, [dispatch]);

  if (!notice) return null;

  return (
    <div className="render-complete-notice-layer" role="status" aria-live="polite">
      <div className="render-complete-notice">
        <CustomToast
          title=""
          content={notice.message}
          actionLabel={notice.renderHistoryPath ? "View Render History" : undefined}
          onAction={
            notice.renderHistoryPath
              ? () => {
                  navigate(notice.renderHistoryPath);
                  setNotice(null);
                }
              : undefined
          }
        />
        <button
          type="button"
          className="render-complete-notice-close"
          aria-label="Dismiss render notification"
          onClick={() => setNotice(null)}
        >
          ✖
        </button>
      </div>
    </div>
  );
}
